<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Edit Event
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.name"
                outlined
                label="Event Name"
                prepend-inner-icon="mdi-calendar-text"
                :rules="[(v) => !!v || 'Event name is required']"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-select
                v-model="form.type"
                :items="eventTypes"
                outlined
                label="Event Type"
                prepend-inner-icon="mdi-calendar-star"
                :rules="[(v) => !!v || 'Event type is required']"
                disabled
              ></v-select>
            </v-col>
            <v-col sm="4">
              <v-switch
                v-model="form.is_active"
                label="Active"
                color="primary"
              ></v-switch>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="menu_start"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.start_date"
                    label="Start Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    readonly
                    :rules="[(v) => !!v || 'Start date is required']"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.start_date"
                  @input="menu_start = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="menu_end"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.end_date"
                    label="End Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    readonly
                    :rules="[(v) => !!v || 'End date is required']"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.end_date"
                  :min="form.start_date"
                  @input="menu_end = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col sm="4" v-if="selectedRewardOption">
              <v-select
                v-model="selectedRewardOption"
                :items="availableRewardOptions"
                outlined
                label="Reward Option"
                prepend-inner-icon="mdi-trophy"
                disabled
              ></v-select>
            </v-col>

            <v-col sm="12" v-if="selectedRewardOption">
              <v-divider></v-divider>
              <h3 class="mb-3">Event Reward Configuration</h3>
            </v-col>

            <!-- Group Keys Section -->
            <v-col sm="12" v-if="selectedRewardOption">
              <template v-if="groupKeys.length > 0">
                <v-card
                  v-for="(group, groupIndex) in groupKeys"
                  :key="`group_${groupIndex}`"
                  class="mb-6"
                  outlined
                >
                  <v-card-title class="py-3 d-flex justify-space-between align-center">
                    <span class="subtitle-1">{{ getGroupKeyLabel() }} #{{ groupIndex + 1 }}</span>
                    <v-btn icon small color="error" @click="removeGroupKey(groupIndex)">
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-card-title>

                  <v-card-text>
                    <!-- Group Key Fields -->
                    <v-row>
                      <!-- Target Reward & Agency Target Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'target_reward' || selectedRewardOption === 'agency_target_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.target"
                          outlined
                          dense
                          label="Target *"
                          type="number"
                          prepend-inner-icon="mdi-target"
                          :rules="[(v) => !!v || 'Target is required']"
                        />
                      </v-col>

                      <!-- Active Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'active_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.time"
                          outlined
                          dense
                          label="Time (minutes) *"
                          type="number"
                          prepend-inner-icon="mdi-clock"
                          :rules="[(v) => !!v || 'Time is required']"
                        />
                      </v-col>

                      <!-- Invitation Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'invitation_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.no_of_hosts"
                          outlined
                          dense
                          label="Number of Hosts *"
                          type="number"
                          prepend-inner-icon="mdi-account-multiple"
                          :rules="[(v) => !!v || 'Number of hosts is required']"
                        />
                      </v-col>

                      <!-- Daily Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'daily_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.daily_target"
                          outlined
                          dense
                          label="Daily Target *"
                          type="number"
                          prepend-inner-icon="mdi-calendar-today"
                          :rules="[(v) => !!v || 'Daily target is required']"
                        />
                      </v-col>

                      <!-- Monthly Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'monthly_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.monthly_target"
                          outlined
                          dense
                          label="Monthly Target *"
                          type="number"
                          prepend-inner-icon="mdi-calendar-month"
                          :rules="[(v) => !!v || 'Monthly target is required']"
                        />
                      </v-col>

                      <!-- Ranking Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'ranking_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.rank_position"
                          outlined
                          dense
                          label="Rank Position *"
                          type="number"
                          prepend-inner-icon="mdi-medal"
                          :rules="[(v) => !!v || 'Rank position is required']"
                        />
                      </v-col>

                      <!-- Performance Reward -->
                      <template v-if="selectedRewardOption === 'performance_reward'">
                        <v-col sm="4">
                          <v-text-field
                            v-model="group.groupKey.performance_metric"
                            outlined
                            dense
                            label="Performance Metric *"
                            prepend-inner-icon="mdi-chart-line"
                            :rules="[(v) => !!v || 'Performance metric is required']"
                          />
                        </v-col>
                        <v-col sm="4">
                          <v-text-field
                            v-model.number="group.groupKey.performance_target"
                            outlined
                            dense
                            label="Target *"
                            type="number"
                            prepend-inner-icon="mdi-target"
                            :rules="[(v) => !!v || 'Target is required']"
                          />
                        </v-col>
                      </template>
                    </v-row>

                    <v-divider class="my-3"></v-divider>

                    <!-- Reward Entries for this Group Key -->
                    <h4 class="mb-3">Reward Entries</h4>

                    <template v-if="group.rewardEntries.length > 0">
                      <v-row
                        v-for="(entry, entryIndex) in group.rewardEntries"
                        :key="`entry_${groupIndex}_${entryIndex}`"
                        class="mb-2"
                      >
                        <v-col sm="3" v-if="!entry.store_item_id || entry.store_item_id === ''">
                          <v-text-field
                            v-model.number="entry.reward_value"
                            outlined
                            dense
                            label="Reward Value"
                            type="number"
                            prepend-inner-icon="mdi-bitcoin"
                            @input="onRewardValueChange(entry)"
                          />
                        </v-col>

                        <v-col sm="3" v-if="!entry.reward_value || entry.reward_value === '' || entry.reward_value === 0">
                          <v-autocomplete
                            v-model="entry.store_item_id"
                            :items="storeItems"
                            item-text="name"
                            item-value="id"
                            outlined
                            dense
                            label="Store Item"
                            prepend-inner-icon="mdi-package-variant"
                            clearable
                            @change="onStoreItemChange(entry)"
                          />
                        </v-col>

                        <v-col sm="3" v-if="entry.store_item_id">
                          <v-text-field
                            v-model="entry.expiry_date"
                            outlined
                            dense
                            label="Expiry Date"
                            type="date"
                            prepend-inner-icon="mdi-calendar-clock"
                            :min="minDate"
                            required
                          />
                        </v-col>

                        <v-col sm="1" class="d-flex align-center">
                          <v-btn icon small color="error" @click="removeRewardEntry(groupIndex, entryIndex)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </template>

                    <v-row>
                      <v-col>
                        <v-btn color="primary" dark small @click="addRewardEntry(groupIndex)">
                          <v-icon left>mdi-plus</v-icon> Add Reward Entry
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>

              <!-- Add Group Key Button -->
              <v-row>
                <v-col>
                  <v-btn color="green" dark @click="addGroupKey">
                    <v-icon left>mdi-plus-circle</v-icon> Add {{ getGroupKeyLabel() }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>

            <v-col sm="12" v-if="participants.length > 0">
              <v-divider class="my-3"></v-divider>
              <h3 class="mb-3">Event Participants</h3>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Participant</th>
                      <th>Type</th>
                      <th>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(participant, index) in participants" :key="index">
                      <td>{{ participant.rank_position || '-' }}</td>
                      <td>{{ participant.user?.name || participant.agency?.name || '-' }}</td>
                      <td>{{ participant.user_id ? 'User' : 'Agency' }}</td>
                      <td>{{ participant.score || 0 }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Update Event
          </v-btn>
          <v-btn rounded class="ml-3" @click="$router.back()"> Cancel </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";

export default {
  name: "EventIdPage",
  data() {
    return {
      form: {},
      eventTypes: [
        { value: "host", text: "Host Event" },
        { value: "agent", text: "Agent Event" },
        { value: "recharge", text: "Recharge Event" },
        { value: "weekly_star", text: "Weekly Star Event" },
      ],
      rewardOptions: {
        host: [
          { value: "target_reward", text: "Target Reward" },
          { value: "active_reward", text: "Active Reward" },
        ],
        agent: [
          { value: "agency_target_reward", text: "Agency Target Reward" },
          { value: "invitation_reward", text: "Invitation Reward" },
        ],
        recharge: [
          { value: "daily_reward", text: "Daily Reward" },
          { value: "monthly_reward", text: "Monthly Reward" },
        ],
        weekly_star: [
          { value: "ranking_reward", text: "Ranking Reward" },
          { value: "performance_reward", text: "Performance Reward" },
        ],
      },
      selectedRewardOption: "",
      groupKeys: [],
      participants: [],
      storeItems: [],
      isLoading: false,
      menu_start: false,
      menu_end: false,
      old_json: {},
    };
  },
  computed: {
    minDate() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split("T")[0];
    },
    availableRewardOptions() {
      if (!this.form.type) return [];
      return this.rewardOptions[this.form.type] || [];
    },
  },
  mounted() {
    this.getData();
    this.getStoreItems();
  },
  methods: {
    getGroupKeyLabel() {
      const labels = {
        target_reward: "Target Reward",
        active_reward: "Active Reward",
        agency_target_reward: "Agency Target Reward",
        invitation_reward: "Invitation Reward",
        daily_reward: "Daily Reward",
        monthly_reward: "Monthly Reward",
        ranking_reward: "Ranking Reward",
        performance_reward: "Performance Reward",
      };
      return labels[this.selectedRewardOption] || "Group";
    },
    async getStoreItems() {
      try {
        const response = await axios.get("/masters?context=reward_category");
        this.storeItems = response.data.storeItem || [];
      } catch (e) {
        console.error("Error loading store items:", e);
      }
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.eventId;

      try {
        // Get event data
        const eventResponse = await axios.get(`/events/${encryptedId}`);
        this.form = eventResponse.data.data;
        this.old_json = eventResponse.data.data;

        // Get event rewards
        const rewardsResponse = await axios.get(`/events/${encryptedId}/rewards`);
        const rewards = rewardsResponse.data.data || [];

        // Parse existing rewards into groupKeys structure
        this.parseRewardsIntoGroupKeys(rewards);

        // Get event participants
        const participantsResponse = await axios.get(`/events/${encryptedId}/participants`);
        this.participants = participantsResponse.data.data || [];
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$toast?.error("Error loading event data");
      }

      this.isLoading = false;
    },
    parseRewardsIntoGroupKeys(rewards) {
      if (rewards.length === 0) return;

      // Determine reward type from first reward
      const firstReward = rewards[0];
      this.selectedRewardOption = firstReward.reward_type;

      // Group rewards by requirement_json
      const groupedRewards = {};

      rewards.forEach(reward => {
        const key = JSON.stringify(reward.requirement_json);
        if (!groupedRewards[key]) {
          groupedRewards[key] = {
            groupKey: this.parseGroupKeyFromJson(reward.requirement_json),
            rewardEntries: []
          };
        }

        // Convert expiry_days to expiry_date
        let expiryDate = "";
        if (reward.expiry_days && reward.store_item_id) {
          const today = new Date();
          const expiry = new Date(today);
          expiry.setDate(expiry.getDate() + parseInt(reward.expiry_days));
          expiryDate = expiry.toISOString().split('T')[0];
        }

        groupedRewards[key].rewardEntries.push({
          id: reward.id,
          reward_value: reward.reward_value || null,
          store_item_id: reward.store_item_id || null,
          expiry_date: expiryDate,
        });
      });

      this.groupKeys = Object.values(groupedRewards);
    },
    parseGroupKeyFromJson(requirementJson) {
      return {
        target: requirementJson?.target || null,
        time: requirementJson?.time || null,
        no_of_hosts: requirementJson?.no_of_hosts || null,
        daily_target: requirementJson?.daily_target || null,
        monthly_target: requirementJson?.monthly_target || null,
        rank_position: requirementJson?.rank_position || null,
        performance_metric: requirementJson?.performance_metric || "",
        performance_target: requirementJson?.performance_target || null,
      };
    },
    addGroupKey() {
      this.groupKeys.push({
        groupKey: {
          target: null,
          time: null,
          no_of_hosts: null,
          daily_target: null,
          monthly_target: null,
          rank_position: null,
          performance_metric: "",
          performance_target: null,
        },
        rewardEntries: [],
      });
    },
    removeGroupKey(groupIndex) {
      this.groupKeys.splice(groupIndex, 1);
    },
    addRewardEntry(groupIndex) {
      this.groupKeys[groupIndex].rewardEntries.push({
        reward_value: null,
        store_item_id: null,
        expiry_date: "",
      });
    },
    removeRewardEntry(groupIndex, entryIndex) {
      this.groupKeys[groupIndex].rewardEntries.splice(entryIndex, 1);
    },
    onRewardValueChange(entry) {
      if (entry.reward_value && entry.reward_value !== "" && entry.reward_value !== 0) {
        entry.store_item_id = null;
        entry.expiry_date = "";
      }
    },
    onStoreItemChange(entry) {
      if (entry.store_item_id) {
        entry.reward_value = null;
        if (!entry.expiry_date) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          entry.expiry_date = tomorrow.toISOString().split('T')[0];
        }
      }
    },
    async save() {
      try {
        if (!this.validateForm()) {
          this.$toast?.error("Please fill all required fields") || alert("Please fill all required fields");
          return;
        }

        if (!this.selectedRewardOption) {
          this.$toast?.error("Event must have a reward option") || alert("Event must have a reward option");
          return;
        }

        if (this.groupKeys.length === 0) {
          this.$toast?.error("Please add at least one group key configuration") || alert("Please add at least one group key configuration");
          return;
        }

        this.isLoading = true;

        // Update event basic info
        const updateData = {
          name: this.form.name,
          start_date: this.form.start_date,
          end_date: this.form.end_date,
          is_active: this.form.is_active,
        };

        await axios.put(`/events/${this.$route.params.eventId}`, updateData);

        // Build all rewards from group keys
        const allRewards = [];

        for (const group of this.groupKeys) {
          const requirement_json = this.buildRequirementJson(group.groupKey);

          for (const entry of group.rewardEntries) {
            if (!entry.reward_value && !entry.store_item_id) continue;

            allRewards.push({
              reward_type: this.selectedRewardOption,
              requirement_json: requirement_json,
              reward_value: entry.reward_value || null,
              store_item_id: entry.store_item_id || null,
              expiry_date: entry.expiry_date || null,
            });
          }
        }

        // Delete old rewards and create new ones
        // Note: This is a simple approach - delete all and recreate
        // You might want to implement a more sophisticated update logic
        for (const reward of allRewards) {
          await axios.post(`/events/${this.$route.params.eventId}/rewards`, reward);
        }

        await this.saveUserTimestamp();
        this.$toast?.success("Event updated successfully!") || alert("Event updated successfully!");
      } catch (e) {
        console.error(e);
        this.$toast?.error("Error updating event") || alert("Error updating event");
      } finally {
        this.isLoading = false;
      }
    },
    buildRequirementJson(groupKey) {
      const requirement_json = {};

      if (this.selectedRewardOption === 'target_reward' || this.selectedRewardOption === 'agency_target_reward') {
        requirement_json.target = groupKey.target;
      } else if (this.selectedRewardOption === 'active_reward') {
        requirement_json.time = groupKey.time;
      } else if (this.selectedRewardOption === 'invitation_reward') {
        requirement_json.no_of_hosts = groupKey.no_of_hosts;
      } else if (this.selectedRewardOption === 'daily_reward') {
        requirement_json.daily_target = groupKey.daily_target;
      } else if (this.selectedRewardOption === 'monthly_reward') {
        requirement_json.monthly_target = groupKey.monthly_target;
      } else if (this.selectedRewardOption === 'ranking_reward') {
        requirement_json.rank_position = groupKey.rank_position;
      } else if (this.selectedRewardOption === 'performance_reward') {
        requirement_json.performance_metric = groupKey.performance_metric;
        requirement_json.performance_target = groupKey.performance_target;
      }

      return requirement_json;
    },
    validateForm() {
      return !!(
        this.form.name &&
        this.form.type &&
        this.form.start_date &&
        this.form.end_date
      );
    },
    async saveUserTimestamp() {
      try {
        let form = {
          from_path: `/events/${this.form.id}`,
          from_name: "EventIdPage",
          user_id: this.user?.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
        this.$router.push("/events");
      } catch (error) {
        console.log(error);
        this.$router.push("/events");
      }
    },
  },
};
</script>
